import { useState, useRef, useEffect } from "react";

import toast from "react-hot-toast";
import styled from "styled-components";

import { REJECT_TRANSACTION_OPTIONS, NOT_READY_TRANSACTION_OPTIONS } from "../../types";

import { trackAction } from "~/analytics";
import { ButtonLink } from "~/components/Button";
import { Separator } from "~/components/Elements";
import * as Form from "~/components/Form";
import Loading from "~/components/Loading";
import Modal from "~/components/Modal";
import { RadioButton } from "~/components/RadioButton";
import Spacer, { Axis } from "~/components/Spacer";
import { Title } from "~/components/Text";
import { postTxConfirm, postTxReject, postTxNotReady, postTxBulk } from "~/endpoints/transactions";
import { Transaction, TransactionType } from "~/endpoints/transactions/types";
import { TransactionWrap } from "~/hooks/use-transaction";
import { THEME_BASE } from "~/styles/theme";

export const YellowBox = styled.div`
  background: ${THEME_BASE.colors.lightYellow};
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0px 0px 2px 0px rgb(0 0 0 / 25%);
`;
const CenteredText = styled.div`
  text-align: center;
`;
const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ReasonWarning = styled.div`
  font-size: 12px;
`;

const CustomSpacer = styled.div`
  height: 12px;
  margin-left: -25px;
  margin-right: -25px;
  margin-top: 5px;
  background-color: rgba(0, 0, 0, 0.86);
`;

const NotReadyCTA = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
`;

const NotReadyButton = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #8e8e8e;
  text-decoration: underline;
  cursor: pointer;
`;

const CustomReasonInput = styled.input`
  height: 25px;
  width: 80%;
  border: 1px solid #babcba;
  border-radius: 4px;
  margin-left: 25px;
`;

type ConfirmIncomingTxModalProps = {
  transaction: TransactionWrap;
  onClose: () => void;
  onAddressConfirmed?: (updatedTransaction: Transaction) => void;
};

export default function ConfirmIncomingTxModal({
  transaction,
  onClose,
  onAddressConfirmed,
}: ConfirmIncomingTxModalProps) {
  const [isBulk, setIsBulk] = useState(false);
  const [reason, setReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [showRejectedModal, setShowRejectedModal] = useState<boolean>(false);
  const [showNotReadyModal, setShowNotReadyModal] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const trackProps = transaction?.beneficiaryVASPdid ? { group_vasp: transaction.beneficiaryVASPdid } : undefined;

  useEffect(() => {
    if (reason === "Other") {
      inputRef?.current?.focus();
    }
  }, [reason]);

  const confirmAddress = async (transactionId: string) => {
    /*This is the regular flow for our customers */
    let updatedTransaction, toastMessage;
    if (isBulk) {
      const updatedTransactions = (await postTxBulk({
        action: "confirm",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
        destinationAddress: transaction.beneficiaryBlockchainAddress,
      })) as Transaction[];
      updatedTransaction = updatedTransactions.find((t) => t.id === transactionId);
      toastMessage = `Blockchain address and ${updatedTransactions.length} transfers confirmed`;
    } else {
      updatedTransaction = await postTxConfirm(transactionId);
      toastMessage = "Blockchain address and transfer confirmed";
    }
    setIsLoading(false);
    if (onAddressConfirmed && updatedTransaction) {
      toast.success(toastMessage);
      trackAction("user-confirmed-blockchain-address", trackProps);
      onAddressConfirmed(updatedTransaction);
    } else {
      // eslint-disable-next-line no-console
      console.error("transfer was provided but the parent component didn't provide the onAddressConfirmed handler");
    }
  };

  const rejectAddress = async (transactionId: string, rejectReason: string) => {
    /*This is the regular flow for our customers */
    let toastMessage;
    if (isBulk) {
      const updatedTransactions = await postTxBulk({
        action: "reject",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
        destinationAddress: transaction.beneficiaryBlockchainAddress,
        reason: rejectReason,
      });
      toastMessage = `Blockchain address and ${updatedTransactions.length} transfers rejected`;
    } else {
      await postTxReject({ id: transactionId, reason });
      toastMessage = "Blockchain address and transfer rejected";
    }
    setIsLoading(false);
    toast.success(toastMessage);
    trackAction("user-rejected-blockchain-address-transaction-rejected", trackProps);
  };

  const applyNotReadyStatus = async (transactionId: string, notReadyReason: string) => {
    let toastMessage;
    if (isBulk) {
      const updatedTransactions = await postTxBulk({
        action: "notReady",
        originatorVASPdid: transaction.originatorVASPdid,
        beneficiaryVASPdid: transaction.beneficiaryVASPdid,
        destinationAddress: transaction.beneficiaryBlockchainAddress,
        reason: notReadyReason,
      });
      toastMessage = `${updatedTransactions.length} transfers not ready to respond`;
    } else {
      await postTxNotReady({ id: transactionId, reason: notReadyReason });
      toastMessage = "Transfer not ready to respond";
    }
    setIsLoading(false);
    toast.success(toastMessage);
    trackAction("user-marked-transaction-not-ready-to-respond", trackProps);
  };

  const handleClickConfirm = async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      if (transaction) {
        await confirmAddress(transaction.id);
      }
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
      setIsLoading(false);
    }
  };

  const handleClickReject = async () => {
    if (isLoading || !transaction) return;
    try {
      setIsLoading(true);
      if (transaction) {
        await rejectAddress(transaction.id, reason);
      }
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
      setIsLoading(false);
    }
    onClose();
  };

  const handleClickNotReady = async () => {
    if (isLoading || !transaction) return;
    try {
      setIsLoading(true);
      await applyNotReadyStatus(transaction.id, reason !== "Other" ? reason : customReason);
    } catch (err) {
      toast.error(`Oops an unexpected error occurred: ${err} `);
      setIsLoading(false);
    }
    onClose();
  };

  const ConfirmAddressModal = (
    <>
      <Modal active onClose={onClose}>
        <Modal.Header>
          <CenteredText>
            <Title as="h1">Is this your company’s blockchain address?</Title>
          </CenteredText>
        </Modal.Header>

        <Modal.Body styles={{ maxWidth: 1000 }}>
          {isLoading && <Loading />}
          <YellowBox>
            <CenteredText>{transaction?.beneficiaryBlockchainAddress}</CenteredText>
          </YellowBox>
          <Spacer axis={Axis.VERTICAL} size={42} />
          <CenteredText>
            Before receiving the transfer details, confirm that this blockchain address belongs to your company.
          </CenteredText>

          <Separator color="link" weight="light" />
          {transaction.type === TransactionType.TRAVELRULE && (
            <Form.CheckboxLabel>
              <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
              <Form.CheckboxText>
                Confirm this blockchain address in all data transfers remaining in Inbox
              </Form.CheckboxText>
            </Form.CheckboxLabel>
          )}
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink
              data-intercom-target="transaction-reject-incoming-tourid-447932"
              color="danger"
              onClick={() => setShowRejectedModal(true)}
            >
              Reject
            </ButtonLink>
            <ButtonLink
              data-intercom-target="transaction-confirm-incoming-tourid-447932"
              color="success"
              onClick={handleClickConfirm}
            >
              Confirm
            </ButtonLink>
          </SpaceBetween>
          <>
            <CustomSpacer />
            <NotReadyCTA>
              <NotReadyButton onClick={() => setShowNotReadyModal(true)}>Not ready to respond?</NotReadyButton>
            </NotReadyCTA>
          </>
        </Modal.Footer>
      </Modal>
    </>
  );

  const RejectTransferModal = (
    <>
      <Modal active onClose={onClose}>
        <Modal.Header>
          <Title as="h1" align="centered">
            Reject this Travel Rule transfer
          </Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Loading />}
          <YellowBox>
            <CenteredText>{transaction?.beneficiaryBlockchainAddress}</CenteredText>
          </YellowBox>
          <Spacer axis={Axis.VERTICAL} size={20} />
          {"Please let us know why you want to reject this transfer:"}
          <RadioButton values={REJECT_TRANSACTION_OPTIONS} onChangeValue={(e) => setReason(e.target.value)} />
          <Spacer axis={Axis.VERTICAL} size={12} />
          <ReasonWarning>{`This information will be accessible by your counterparty`}</ReasonWarning>

          <Separator color="link" weight="light" />
          <Form.CheckboxLabel>
            <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
            <Form.CheckboxText>
              Reject all data transfers with this blockchain address remaining in the Inbox
            </Form.CheckboxText>
          </Form.CheckboxLabel>
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink onClick={() => setShowRejectedModal(false)}>{"Take me back"}</ButtonLink>
            <ButtonLink disabled={!reason} color="danger" onClick={handleClickReject}>
              Reject
            </ButtonLink>
          </SpaceBetween>
        </Modal.Footer>
      </Modal>
    </>
  );

  const NotReadyToRespondModal = (
    <>
      <Modal active onClose={onClose}>
        <Modal.Header>
          <Title as="h1" align="centered">
            Not ready to respond to data transfers yet?
          </Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <Loading />}
          {"No worries. Select the reason below and we will let your counterparty know."}
          <RadioButton
            values={NOT_READY_TRANSACTION_OPTIONS}
            onChangeValue={(e) => {
              setReason(e.target.value);
              setCustomReason("");
            }}
          />
          <CustomReasonInput
            ref={inputRef}
            value={customReason}
            disabled={reason !== "Other"}
            onChange={(e) => setCustomReason(e.target.value)}
          />
          <Spacer axis={Axis.VERTICAL} size={12} />
          <ReasonWarning>{`This information will be accessible by your counterparty`}</ReasonWarning>

          <Separator color="link" weight="light" />
          <Form.CheckboxLabel>
            <Form.CheckboxInput checked={isBulk} onChange={(e) => setIsBulk(e.target.checked)}></Form.CheckboxInput>
            <Form.CheckboxText>Mark all my incoming data transfers as “Not ready to respond”</Form.CheckboxText>
          </Form.CheckboxLabel>
        </Modal.Body>

        <Modal.Footer>
          <SpaceBetween>
            <ButtonLink
              onClick={() => {
                setCustomReason("");
                setShowNotReadyModal(false);
              }}
            >
              {"Back"}
            </ButtonLink>
            <ButtonLink
              disabled={!reason || (reason === "Other" && customReason === "")}
              color="success"
              onClick={handleClickNotReady}
            >
              Done
            </ButtonLink>
          </SpaceBetween>
        </Modal.Footer>
      </Modal>
    </>
  );

  return showRejectedModal ? RejectTransferModal : showNotReadyModal ? NotReadyToRespondModal : ConfirmAddressModal;
}
